// Klarspråkslager: hur man rent praktiskt gör varje dokument i Bas-processen.
// Håller språket enkelt för den som inte jobbar med arbetsmiljö dagligen.

export interface DocHowTo {
  /** 3-5 korta steg i klarspråk */
  how: string[];
  /** Vad/vem du behöver innan du börjar */
  needs: string;
  /** När dokumentet räknas som klart */
  doneWhen: string;
}

export const DOC_HOWTO: Record<string, DocHowTo> = {
  "00": {
    how: [
      "Läs igenom listan över dokumenten och i vilken ordning de kommer.",
      "Bocka av vilka dokument som är aktuella i ditt projekt.",
      "Peka ut vem som äger varje dokument.",
    ],
    needs: "Projektets namn, storlek och vilka roller som är utsedda.",
    doneWhen: "Alla aktuella dokument har en ansvarig och en ordning.",
  },
  "01": {
    how: [
      "Beskriv kort hur ni arbetar med arbetsmiljö i projektet: möten, uppföljning och avvikelser.",
      "Ange vem som gör vad och hur ofta.",
      "Exportera till Word och lägg den överst i dokumentpärmen.",
    ],
    needs: "Vilka roller som finns och hur ofta ni har möten.",
    doneWhen: "Rutinen är ifylld och delad med projektets roller.",
  },
  "02": {
    how: [
      "Gå igenom utrymme för utrymme: hur ska det städas, servas och underhållas?",
      "Markera de krav som gäller och lägg till egna där något saknas.",
      "Skicka kraven vidare till projektörerna.",
    ],
    needs: "Ritningar eller en lista över byggnadens utrymmen.",
    doneWhen: "Varje utrymme har ett svar och inga fält står tomma.",
  },
  "03": {
    how: [
      "Fyll i projektets storlek, typ och tidplan.",
      "Låt systemet föreslå SKR-profil (storlek, komplexitet, risk).",
      "Justera profilen om du vet något som systemet inte kan se.",
    ],
    needs: "Kontraktssumma eller yta, byggtid och kort beskrivning av arbetet.",
    doneWhen: "SKR-profilen är satt och sparad.",
  },
  "04": {
    how: [
      "Kontrollera om projektet passerar gränsen: mer än 30 arbetsdagar och 20 personer, eller 500 persondagar.",
      "Gör anmälan hos Arbetsmiljöverket.",
      "Ladda upp kvittensen här och sätt datum.",
    ],
    needs: "Byggherrens uppgifter, byggstart, byggtid och antal personer.",
    doneWhen: "Kvittensen är uppladdad och anmälan är anslagen på arbetsplatsen.",
  },
  "05": {
    how: [
      "Välj vem som ska vara Bas-P och Bas-U.",
      "Beskriv uppdragets omfattning och kontrollera kompetensen.",
      "Exportera till Word och låt byggherren skriva under.",
    ],
    needs: "Namn, företag och kompetensbevis för de utsedda.",
    doneWhen: "Både Bas-P och Bas-U är utsedda skriftligt.",
  },
  "06": {
    how: [
      "Lägg in tidplanens huvudmoment med start- och slutdatum.",
      "Se var moment krockar i tid och rum.",
      "Skriv en kommentar om vad som behöver flyttas eller samordnas.",
    ],
    needs: "Projektets tidplan, minst på aktivitetsnivå.",
    doneWhen: "Alla krockar är kommenterade eller lösta.",
  },
  "07": {
    how: [
      "Lista vilka som påverkas: boende, personal, besökare, trafik.",
      "Beskriv skydd, arbetstider och information för varje grupp.",
      "Peka ut vem som ansvarar för kontakten.",
    ],
    needs: "Information om pågående verksamhet i och runt arbetsområdet.",
    doneWhen: "Varje berörd grupp har en åtgärd och en ansvarig.",
  },
  "08": {
    how: [
      "Registrera vilka material som misstänks eller är konstaterade (asbest, PCB, m.m.).",
      "Ladda upp provsvar och saneringsintyg.",
      "Markera saneringen som klar innan rivning startar.",
    ],
    needs: "Materialinventering och eventuella provsvar.",
    doneWhen: "Inga poster ligger kvar som misstänkta utan provsvar.",
  },
  "09": {
    how: [
      "Beskriv vad som ska kopplas ifrån: el, tryck, gas.",
      "Ange vem som är behörig och hur läget låses (LOTO).",
      "Kvittera att spänningslöshet är kontrollerad innan arbete startar.",
    ],
    needs: "Behörig elektriker eller driftansvarig och uppgift om anläggningen.",
    doneWhen: "Frånkopplingen är kvitterad av behörig person.",
  },
  "10": {
    how: [
      "Gå igenom arbetsområdet punkt för punkt: avgränsning, grannar, installationer.",
      "Svara ja/nej/ej aktuellt och skriv en kort kommentar där det behövs.",
      "Ladda upp foton eller kartor som stöd.",
    ],
    needs: "Platsbesök eller karta/ritning över området.",
    doneWhen: "Alla punkter är besvarade och de som är ja har en åtgärd.",
  },
  "11": {
    how: [
      "Gå igenom frågorna och markera vilka risker som finns i projektet.",
      "Skriv åtgärd, ansvarig och senaste datum för varje risk.",
      "Följ upp status löpande – listan lever hela projektet.",
    ],
    needs: "Arbetsområdesutredningen och projektörernas kvarvarande risker.",
    doneWhen: "Varje identifierad risk har åtgärd, ansvarig och status.",
  },
  "12": {
    how: [
      "Låt systemet förifylla AMP från risker, SKR och arbetsområdesutredningen.",
      "Komplettera organisation, ordningsregler och särskilda risker.",
      "Exportera till Word och anslå planen på arbetsplatsen före byggstart.",
    ],
    needs: "Dok 10, 11 och 13 ifyllda samt utsedda Bas-P och Bas-U.",
    doneWhen: "AMP är komplett, exporterad och tillgänglig på arbetsplatsen.",
  },
  "13": {
    how: [
      "Gå igenom de 13 särskilda riskerna.",
      "Markera vilka som förekommer och beskriv åtgärden.",
      "Resultatet flyttas automatiskt in i arbetsmiljöplanen.",
    ],
    needs: "Kännedom om arbetsmomenten i projektet.",
    doneWhen: "Alla 13 punkter är besvarade med ja eller nej.",
  },
  "14": {
    how: [
      "Granska handlingarna med arbetsmiljöglasögon.",
      "Notera vad som behöver åtgärdas innan utförandet.",
      "Kvittera granskningen med datum och namn.",
    ],
    needs: "Färdiga projekteringshandlingar.",
    doneWhen: "Granskningen är kvitterad och synpunkterna är omhändertagna.",
  },
  "15": {
    how: [
      "Utgå från standardreglerna och stryk det som inte gäller.",
      "Lägg till projektspecifika regler.",
      "Exportera och gå igenom reglerna vid introduktionen.",
    ],
    needs: "Kännedom om arbetsplatsens förutsättningar.",
    doneWhen: "Reglerna är beslutade och delade med alla entreprenörer.",
  },
  "15c": {
    how: [
      "Registrera varje person som får introduktion: namn, företag och språk.",
      "Bocka av vad introduktionen omfattade.",
      "Spara – listan blir ditt bevis på att alla är informerade.",
    ],
    needs: "Arbetsmiljöplanen och ordningsreglerna klara.",
    doneWhen: "Alla som arbetar på plats finns i listan.",
  },
  "16": {
    how: [
      "Välj din disciplin (el, VVS, konstruktion m.m.).",
      "Redovisa de risker du inte kunnat projektera bort, för både bygg- och bruksskede.",
      "Skicka in – Bas-P kvitterar och för in dem i risklistan.",
    ],
    needs: "Dina handlingar och kännedom om valda lösningar.",
    doneWhen: "Inlämningen är skickad och kvitterad av Bas-P.",
  },
  "20": {
    how: [
      "Gå igenom AMP, risklista och öppna frågor tillsammans med Bas-U.",
      "Notera vad som kvarstår och vem som tar över det.",
      "Båda kvitterar överlämningen.",
    ],
    needs: "AMP och risksammanställning klara.",
    doneWhen: "Både Bas-P och Bas-U har kvitterat.",
  },
  "21": {
    how: [
      "Välj vilka projekterade åtgärder som ska kontrolleras.",
      "Notera om de är genomförda på plats.",
      "Skapa åtgärd med ansvarig där något saknas.",
    ],
    needs: "Risksammanställningen och tillträde till arbetsplatsen.",
    doneWhen: "Varje punkt är kontrollerad eller har en pågående åtgärd.",
  },
  "22": {
    how: [
      "Samla kvarstående risker och drift- och underhållsunderlag.",
      "Gå igenom dem med byggherren eller förvaltaren.",
      "Kvittera överlämningen med datum.",
    ],
    needs: "Dok 31 och slutdokumentationen.",
    doneWhen: "Byggherren har kvitterat mottagandet.",
  },
  "23": {
    how: [
      "Boka mötet och lägg in deltagarna.",
      "Protokollför risker, ändringar och beslut.",
      "Lägg beslut som får konsekvenser i risklistan.",
    ],
    needs: "Öppna frågor från Bas-P och Bas-U.",
    doneWhen: "Protokollet är sparat och besluten har ansvariga.",
  },
  "24": {
    how: [
      "Beskriv arbetsmomentet med egna ord – systemet föreslår risker och åtgärder.",
      "Komplettera med kompetenser, utrustning och skyddsåtgärder.",
      "Gå igenom beredningen med arbetslaget innan start.",
    ],
    needs: "Vilket moment som ska utföras, av vem och var.",
    doneWhen: "Beredningen är genomgången och kvitterad av arbetslaget.",
  },
  "25": {
    how: [
      "Skriv in frågan och vem den går till.",
      "Svara och sätt status när frågan är löst.",
      "Koppla svaret till risk eller AMP om det påverkar dem.",
    ],
    needs: "Inget – loggen fylls på löpande.",
    doneWhen: "Inga frågor ligger obesvarade vid överlämning.",
  },
  "26": {
    how: [
      "Dela upp projektet i skeden i tid och rum.",
      "Markera var arbeten sker samtidigt.",
      "Beskriv gemensamma skydd för varje skede.",
    ],
    needs: "Tidplan och APD-plan.",
    doneWhen: "Varje skede har beskrivna skyddsåtgärder.",
  },
  "27": {
    how: [
      "Starta en skyddsrond och gå igenom checklistan på plats.",
      "Fotografera avvikelser och sätt ansvarig och datum.",
      "Stäng punkterna när de är åtgärdade.",
    ],
    needs: "Tillträde till arbetsplatsen, gärna med skyddsombud.",
    doneWhen: "Protokollet är signerat och alla avvikelser har ansvarig.",
  },
  "28": {
    how: [
      "Registrera händelsen så snart som möjligt: vad hände, var och vem berördes.",
      "Beskriv orsak och åtgärd så att det inte upprepas.",
      "Anmäl till Arbetsmiljöverket vid allvarlig händelse och notera det här.",
    ],
    needs: "Uppgifter från den som var med eller såg händelsen.",
    doneWhen: "Utredning och åtgärd är ifyllda och ärendet är stängt.",
  },
  "30": {
    how: [
      "Gå igenom risklistan och kontrollera att varje risk är hanterad eller överförd.",
      "Notera vad som lämnas vidare till bruksskedet.",
      "Kvittera slutkontrollen.",
    ],
    needs: "Risksammanställningen och uppföljningen av utförandet.",
    doneWhen: "Inga öppna risker saknar hantering.",
  },
  "31": {
    how: [
      "Samla risker som finns kvar när byggnaden används.",
      "Beskriv hur förvaltaren ska hantera dem.",
      "Exportera och lämna över tillsammans med slutdokumentationen.",
    ],
    needs: "Projektörernas bruksrisker och funktionskraven i Dok 02.",
    doneWhen: "Varje kvarvarande risk har en beskriven hantering.",
  },
  "32": {
    how: [
      "Samla ihop drift- och underhållsunderlag och arbetsmiljödokumentationen.",
      "Kontrollera att inget saknas i listan.",
      "Lämna över paketet till förvaltningen.",
    ],
    needs: "Dok 22, 31 och 34.",
    doneWhen: "Förvaltningen har kvitterat hela paketet.",
  },
  "33": {
    how: [
      "Gå igenom funktionskraven från Dok 02 i den färdiga byggnaden.",
      "Markera uppfyllt eller avvikelse per punkt.",
      "Skapa åtgärd för avvikelser innan överlämning.",
    ],
    needs: "Dok 02 ifylld och tillträde till den färdiga byggnaden.",
    doneWhen: "Alla funktionskrav är verifierade eller har en åtgärd.",
  },
  "34": {
    how: [
      "Låt systemet sammanställa hela arbetsmiljödokumentationen.",
      "Kontrollera att alla dokument finns med.",
      "Ladda ner allt som Word i ett svep och lämna över.",
    ],
    needs: "Att övriga dokument är klara.",
    doneWhen: "Sammanfattningen är exporterad och överlämnad.",
  },
};

export const getHowTo = (docNumber?: string): DocHowTo | undefined => {
  if (!docNumber) return undefined;
  return DOC_HOWTO[docNumber] ?? DOC_HOWTO[docNumber.split(/[^0-9a-zA-Z]/)[0]];
};
